import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
} from '@mui/material';
import { Campaign, AdVariation } from '../models/Campaign';
import { CampaignService } from '../services/firebase';
import { AnalyticsService } from '../services/analytics';
import { VALID_CATEGORIES } from '../utils/standardizeCategories';
import { Delete as DeleteIcon, Preview as PreviewIcon } from '@mui/icons-material';

interface BulkAdVariationDeleteDialogProps {
    open: boolean;
    onClose: () => void;
    campaigns: Campaign[];
}

const BulkAdVariationDeleteDialog: React.FC<BulkAdVariationDeleteDialogProps> = ({
    open,
    onClose,
    campaigns,
}) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedVariationId, setSelectedVariationId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // Filter campaigns based on selected category
    const filteredCampaigns = useMemo(() => {
        return campaigns.filter(campaign => 
            !selectedCategory || campaign.category === selectedCategory
        );
    }, [campaigns, selectedCategory]);

    // Get unique variations across filtered campaigns
    const uniqueVariations = useMemo(() => {
        const variations = new Map<string, AdVariation>();
        
        filteredCampaigns.forEach(campaign => {
            campaign.ad_variations?.forEach(variation => {
                if (!variations.has(variation.id)) {
                    variations.set(variation.id, variation);
                }
            });
        });

        return Array.from(variations.values());
    }, [filteredCampaigns]);

    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
        setSelectedVariationId('');
        setPreviewUrl(null);
    };

    const handlePreviewClick = (variation: AdVariation) => {
        setPreviewUrl(variation.image_url || variation.video_url || null);
    };

    const handleDelete = async () => {
        if (!selectedVariationId) {
            setError('Please select a variation to delete');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // Get all campaigns that have this variation
            const campaignsWithVariation = filteredCampaigns.filter(campaign => 
                campaign.id && campaign.ad_variations?.some(v => v.id === selectedVariationId)
            );

            // Update each campaign that has this variation
            await Promise.all(
                campaignsWithVariation.map(async (campaign) => {
                    if (campaign.id) {
                        const updatedVariations = campaign.ad_variations.filter(
                            v => v.id !== selectedVariationId
                        );
                        
                        const updatedCampaign = {
                            ...campaign,
                            ad_variations: updatedVariations
                        };

                        await CampaignService.updateCampaign(campaign.id, updatedCampaign);
                        
                        // Delete analytics data for this variation from this campaign
                        await AnalyticsService.deleteVariationAnalytics(campaign.id, selectedVariationId);
                    }
                })
            );

            setSuccess(true);
            setTimeout(() => {
                onClose();
                // Reset state
                setSelectedVariationId('');
                setSelectedCategory('');
                setPreviewUrl(null);
            }, 1500);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Delete Ad Variation from Multiple Campaigns</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Ad variation deleted successfully!
                    </Alert>
                )}

                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        <strong>Warning:</strong> Deleting an ad variation will permanently remove it from all selected campaigns 
                        and delete all associated analytics data. This action cannot be undone.
                    </Typography>
                </Alert>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Select Category
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Category Filter</InputLabel>
                        <Select
                            value={selectedCategory}
                            label="Category Filter"
                            onChange={(e) => handleCategoryChange(e.target.value)}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            <MenuItem value={VALID_CATEGORIES.FRANCHISE}>{VALID_CATEGORIES.FRANCHISE}</MenuItem>
                            <MenuItem value={VALID_CATEGORIES.CORPORATE}>{VALID_CATEGORIES.CORPORATE}</MenuItem>
                        </Select>
                    </FormControl>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Select Variation to Delete
                    </Typography>
                    
                    <List>
                        {uniqueVariations.map((variation) => (
                            <React.Fragment key={variation.id}>
                                <ListItem
                                    selected={selectedVariationId === variation.id}
                                    onClick={() => setSelectedVariationId(variation.id)}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={variation.name}
                                        secondary={`Type: ${variation.ad_type || 'Not set'} | Time: ${variation.start_time}-${variation.end_time}`}
                                    />
                                    <ListItemSecondaryAction>
                                        {(variation.image_url || variation.video_url) && (
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => handlePreviewClick(variation)}
                                                sx={{ mr: 1 }}
                                            >
                                                <PreviewIcon />
                                            </IconButton>
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </List>

                    {/* Media Preview */}
                    {previewUrl && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Media Preview
                            </Typography>
                            <Box sx={{ 
                                width: '100%',
                                height: '200px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                            }}>
                                {previewUrl.includes('image') ? (
                                    <img
                                        src={previewUrl}
                                        alt="Ad variation preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <video
                                        src={previewUrl}
                                        controls
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%'
                                        }}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleDelete}
                    variant="contained"
                    color="error"
                    disabled={loading || !selectedVariationId}
                    startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
                >
                    {loading ? 'Deleting...' : 'Delete Variation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkAdVariationDeleteDialog; 